import { passwordSchema, ROLE_LABELS } from "@pcj/shared";
import { KeyRound } from "lucide-react";
import { type FormEvent, useState } from "react";
import { Link } from "react-router";
import { AppHeader } from "../components/AppHeader";
import { Banner, Button, Card, Field, inputClass } from "../components/ui";
import { authClient, authErrorMessage } from "../lib/auth-client";
import { homePath, useCurrentUser } from "./SessionPages";

/** The signed-in user's account: who they are and a password change. */
export function AccountPage() {
  const me = useCurrentUser();
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [status, setStatus] = useState<{ tone: "success" | "error"; message: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (key: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm({ ...form, [key]: e.target.value });

  async function submit(event: FormEvent) {
    event.preventDefault();
    setStatus(null);
    const parsed = passwordSchema.safeParse(form.next);
    if (!parsed.success) {
      return setStatus({ tone: "error", message: parsed.error.issues[0]?.message ?? "Contraseña inválida" });
    }
    if (form.next !== form.confirm) return setStatus({ tone: "error", message: "Las contraseñas no coinciden." });
    setBusy(true);
    const { error } = await authClient.changePassword({
      currentPassword: form.current,
      newPassword: form.next,
      // Any other browser where this account was open must sign in again.
      revokeOtherSessions: true,
    });
    setBusy(false);
    if (error) {
      const message =
        error.code === "INVALID_PASSWORD" ? "La contraseña actual no es correcta." : authErrorMessage(error);
      return setStatus({ tone: "error", message });
    }
    setForm({ current: "", next: "", confirm: "" });
    setStatus({ tone: "success", message: "Contraseña actualizada. Se cerraron tus otras sesiones." });
  }

  return (
    <div className="min-h-screen">
      <AppHeader me={me} title="Mi cuenta" backTo={homePath(me)} />
      <main className="mx-auto max-w-lg space-y-4 px-4 py-8">
        <Card className="p-5 text-sm">
          <p className="font-medium text-gray-900">{me.profile.name ?? me.user.name}</p>
          <p className="text-gray-600">{me.user.email}</p>
          <p className="mt-1 text-xs text-gray-500">
            {ROLE_LABELS[me.profile.role]}
            {me.company && ` · ${me.company.legalName}`}
          </p>
        </Card>
        <Card className="p-5">
          <div className="mb-4 flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-gold-500" aria-hidden />
            <h2 className="text-base font-semibold text-gray-900">Cambiar contraseña</h2>
          </div>
          <form onSubmit={submit} className="space-y-3">
            <Field label="Contraseña actual">
              {(id) => (
                <input
                  id={id}
                  type="password"
                  autoComplete="current-password"
                  required
                  className={inputClass}
                  value={form.current}
                  onChange={set("current")}
                />
              )}
            </Field>
            <Field label="Nueva contraseña" hint="Mínimo 10 caracteres.">
              {(id) => (
                <input
                  id={id}
                  type="password"
                  autoComplete="new-password"
                  required
                  className={inputClass}
                  value={form.next}
                  onChange={set("next")}
                />
              )}
            </Field>
            <Field label="Confirma la nueva contraseña">
              {(id) => (
                <input
                  id={id}
                  type="password"
                  autoComplete="new-password"
                  required
                  className={inputClass}
                  value={form.confirm}
                  onChange={set("confirm")}
                />
              )}
            </Field>
            {status && <Banner tone={status.tone}>{status.message}</Banner>}
            <Button type="submit" loading={busy}>
              Guardar contraseña
            </Button>
          </form>
        </Card>
        <p className="text-xs text-gray-500">
          <Link to={homePath(me)} className="underline hover:text-gray-900">
            Volver al portal
          </Link>
        </p>
      </main>
    </div>
  );
}
